import { Form, Modal, Skeleton } from 'antd';
import React, { useState, useEffect } from 'react';
import { CustomButton, IconButton } from '../../../components';
import {
  getPAInfo,
  getPAPayProducts,
  getPAUnits,
  previewPA,
} from '../../../api/module/ctm_products';
import AjaxTable from '../../../components/AjaxTable';
import { catchException } from '../../../utils/helpers/helpers';
import {
  PAProductSTForm,
  PAProductForm,
} from '../../ProductApproval/PaymentForm';
import { EyeOutlined } from '@ant-design/icons';

function PAPreviewPaymentForm({ record, contract }) {
  const [products, setProducts] = useState();
  const [form] = Form.useForm();

  useEffect(() => {
    async function fetchData() {
      setProducts();
      try {
        const res = await getPAPayProducts(record.id);
        setProducts(
          res.data.reduce((pv, cv) => {
            if (cv.tipo.endsWith('sT'))
              pv[`pst_${cv.producto}`] = { ...cv, id: cv.producto };
            else pv[cv.id] = cv;
            return pv;
          }, {}),
        );
        form.resetFields();
      } catch (error) {
        catchException(error);
      }
    }

    if (!products) fetchData();
  }, [record.id]);

  if (!products) return <Skeleton active loading />;
  return (
    <>
      {!contract.unicamente_sin_tope ? (
        <Form layout="horizontal" form={form} initialValues={products}>
          {Object.entries(products)
            .filter((p) => !p[0].startsWith('pst_'))
            .map((p) => (
              <PAProductForm
                key={p[0]}
                initialValues={p[1]}
                ms={contract.moneda_s}
                currencyType={contract.moneda_sigla}
                disabled={true}
                paymentStatus={record.estado_aprobacion}
                payNumber={record.numero}
              />
            ))}
        </Form>
      ) : null}
      {Object.entries(products)
        .filter((p) => p[0].startsWith('pst_'))
        .map((p) => (
          <PAProductSTForm
            key={p[0]}
            paymentID={record.id}
            initialValues={p[1]}
            ms={contract.moneda_s}
            referenceValue={contract.valor_referencia}
            acumulatedValue={contract.acumulado_referencia}
            disabled={true}
            paymentStatus={record.estado_aprobacion}
            payNumber={record.numero}
            unitsEndpoint={getPAUnits}
          />
        ))}
    </>
  );
}

export default function PaPreviewModal({ contratoID, iconButton = false }) {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState();

  const [expandedKeys, setExpandedKeys] = useState([]);

  const expandedRowRender = (record) => {
    return <PAPreviewPaymentForm record={record} contract={info} />;
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await getPAInfo(contratoID);
        setInfo({
          ...res.data,
          acumulado_referencia: res.data.acumulado_referencia || 0,
        });
      } catch (error) {
        catchException(error);
      }
    }
    if (open && info?.id !== contratoID) fetchData();
  }, [contratoID, open]);

  const paymentColumns = [
    {
      title: 'No. de pago',
      dataIndex: 'numero',
    },
    {
      title: 'Valor del pago (incluyendo impuestos, tasas y contribuciones)',
      width: '16rem',
      dataIndex: 'valor',
      render: (t) =>
        `${info?.moneda_s} ${t
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`,
    },
    {
      title: 'Estado de aprobación',
      dataIndex: 'estado_nombre',
    },
    {
      title: 'Valor real aprobado',
      dataIndex: 'valor_a_pagar',
      render: (t) =>
        t
          ? `${info?.moneda_s} ${t
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
          : null,
    },
    {
      title: 'Acciones',
      key: 'actions',
      fixed: 'right',
      align: 'center',
      render: () => <CustomButton text="Ver" />,
    },
  ];

  const stColumns = [
    {
      title: 'No. de aprobación',
      dataIndex: 'numero',
    },

    {
      title: 'Estado de aprobación',
      dataIndex: 'estado_nombre',
    },
    {
      title: 'Valor para este pago',
      dataIndex: 'valor_a_pagar',
      render: (t) =>
        `${info?.moneda_s} ${(t || 0)
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`,
    },
    {
      title: 'Acciones',
      key: 'actions',
      fixed: 'right',
      align: 'center',
      render: () => <CustomButton text="Ver" />,
    },
  ];

  return (
    <>
      {iconButton ? (
        <IconButton icon={<EyeOutlined />} onClick={() => setOpen(true)} />
      ) : (
        <CustomButton
          type={'default'}
          htmlType={'button'}
          text="Ver detalle de aprobaciones"
          onClick={() => setOpen(true)}
        />
      )}
      <Modal
        title="Aprobación de productos"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        destroyOnClose
        width="80%"
      >
        {info ? (
          <AjaxTable
            columns={info.unicamente_sin_tope ? stColumns : paymentColumns}
            endpoint={previewPA}
            query={info.id}
            expandedRowRender={{
              expandedRowRender,
              expandRowByClick: true,
              indentSize: 5,
              expandedRowKeys: expandedKeys,
              onExpandedRowsChange: (v) => setExpandedKeys(v),
            }}
            onSearch={(ds) => setExpandedKeys(ds.map((v) => v.id))}
            tableTitle="Listado de pagos"
            searchInput
            exportCSV
          />
        ) : (
          <Skeleton loading />
        )}
      </Modal>
    </>
  );
}
